/**
 * Shared styling for inline multiselect (API path and MultiSelectDropdown).
 * Keeps look and behavior identical for both code paths.
 * Tags stay on one line (no wrap); border/underline matches read-only Tag (grey[200]).
 *
 * Also contains scoped cell state styles (hover, edit, error, disabled)
 * per Figma specs for dl-59430.
 */

import { colors } from 'styles/theme/colors';
import { INLINE_POPPER_OPTIONS } from './inlinePopperModifiers';

const inputBorderGrey = colors.grey[200];
const inputBorderError = colors.error[400];

const multiselectInputSx = (borderColor: string) => ({
  '& .MuiInputBase-root': {
    flexWrap: 'nowrap',
    gap: '4px',
    paddingTop: '4px',
    paddingBottom: '4px',
    // Reserve room on the right so chips / the +N indicator never sit under the
    // absolutely-positioned popup caret (DL-60178).
    paddingRight: '28px',
    overflow: 'hidden',
    minWidth: 0,
    borderBottom: '1px solid',
    borderBottomColor: borderColor,
    '&::before': {
      borderBottomColor: borderColor,
    },
    '&::after': {
      borderBottomColor: borderColor,
    },
    '&:hover': {
      borderBottomColor: borderColor,
      '&::before': { borderBottomColor: borderColor },
    },
    '&.Mui-focused': {
      borderBottomColor: borderColor,
      '&::after': { borderBottomColor: borderColor },
    },
  },
});

export const MULTISELECT_INPUT_SX = multiselectInputSx(inputBorderGrey);

/** Red underline variant, used when a server-side save error is shown inline (DL-60176). */
export const MULTISELECT_INPUT_ERROR_SX = multiselectInputSx(inputBorderError);

export const MULTISELECT_PAPER_PROPS = {
  paper: {
    sx: {
      marginTop: '4px',
      maxWidth: 'calc(100vw - 16px)',
      minWidth: 'min(250px, calc(100vw - 16px))',
    },
  },
};

export const MULTISELECT_POPPER_PROPS = {
  popper: {
    placement: 'bottom-start' as const,
    style: { maxWidth: 'calc(100vw - 16px)' },
    popperOptions: INLINE_POPPER_OPTIONS,
  },
};
